import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import '../static/css/SettingsPage.css';
import '../static/css/PauseAccount.css';

class PauseAccount extends Component {
    constructor() {
        super();
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;
        this.state = {
            isPaused: !!(user && user.account_paused),
            isLegacyPricing: !!(user && user.is_premium_community_only && user.is_legacy_pricing),
            showConfirmModal: false,
            isProcessing: false,
            successMessage: '',
            error: '',
        };
        this.openConfirmModal = this.openConfirmModal.bind(this);
        this.closeConfirmModal = this.closeConfirmModal.bind(this);
        this.confirmAction = this.confirmAction.bind(this);
    }

    openConfirmModal() {
        if (this.state.isProcessing) return;
        this.setState({ showConfirmModal: true, error: '', successMessage: '' });
    }

    closeConfirmModal() {
        if (this.state.isProcessing) return;
        this.setState({ showConfirmModal: false });
    }

    async confirmAction() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ error: 'Your session has expired. Please sign in again.', showConfirmModal: false });
            return;
        }
        const wasPaused = this.state.isPaused;
        const endpoint = wasPaused ? '/account/unpause' : '/account/pause';
        this.setState({ isProcessing: true, error: '' });
        try {
            await axios.post(
                process.env.REACT_APP_SERVER_API + endpoint,
                {},
                { headers: { Authorization: 'Bearer ' + token } }
            );
            const userJson = localStorage.getItem('user');
            if (userJson) {
                const user = JSON.parse(userJson);
                user.account_paused = !wasPaused;
                localStorage.setItem('user', JSON.stringify(user));
            }
            this.setState({
                isPaused: !wasPaused,
                isProcessing: false,
                showConfirmModal: false,
                successMessage: wasPaused
                    ? 'Welcome back! Your account is active again.'
                    : 'Your account is now paused. You can unpause anytime.',
            });
        } catch (err) {
            const data = err.response && err.response.data;
            const serverError = data && (data.error || (typeof data === 'string' ? data : ''));
            this.setState({
                isProcessing: false,
                showConfirmModal: false,
                error: serverError || 'We could not complete that action. Please try again.',
            });
        }
    }

    renderActiveState() {
        return (
            <>
                <h3 className="pause-account-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Info</h3>
                <p className="pause-account-paragraph">
                    Pausing your account means you are taking a break and will go silent for the moment without losing your stats, followers, friends, and event history. You will still be able to access your account.
                </p>
                <p className="pause-account-paragraph pause-account-apple-note">
                    If you have a subscription active through Apple, please go into your account through Apple and cancel it.
                </p>

                {this.state.isLegacyPricing && (
                    <div className="pause-account-legacy-warning">
                        <span className="pause-account-legacy-warning-badge" style={{fontFamily: "'Chelsea Market', cursive"}}>
                            <span className="pause-account-legacy-warning-icon">💎</span>
                            Legacy Pricing
                        </span>
                        <p className="pause-account-legacy-warning-text">
                            <strong className="pause-account-legacy-warning-strong">Heads up:</strong> if you cancel your Apple subscription, your locked-in Legacy Pricing will be lost <strong className="pause-account-legacy-warning-strong">forever</strong>. Re-subscribing later will be at current standard rates.
                        </p>
                    </div>
                )}

                <h3 className="pause-account-section-title pause-account-section-title-secondary" style={{fontFamily: "'Chelsea Market', cursive"}}>What Happens When in Pause Mode</h3>
                <ul className="pause-account-list">
                    <li>Your profile will be hidden from other users</li>
                    <li>You will be removed from events you are attending</li>
                    <li>Events you are hosting will be cancelled</li>
                    <li>You will not appear in search results or recommendations</li>
                    <li>Your followers and friends list will be preserved</li>
                    <li>Your event history and stats will be saved</li>
                    <li>You will still be able to use your account but without activity</li>
                    <li>You will not be able to browse events</li>
                </ul>
            </>
        );
    }

    renderPausedState() {
        return (
            <>
                <h3 className="pause-account-section-title pause-account-section-title-paused" style={{fontFamily: "'Chelsea Market', cursive"}}>Your Account Is Paused</h3>
                <p className="pause-account-paragraph">
                    Your profile is currently hidden from other users, and you don't appear in search results or events. Your followers, friends, event history, and stats are all preserved.
                </p>
                <p className="pause-account-paragraph">
                    Unpause anytime to reactivate your account and become visible to the community again.
                </p>
            </>
        );
    }

    renderConfirmModal() {
        const { isPaused, isProcessing } = this.state;
        const isUnpausing = isPaused;
        const title = isUnpausing ? 'Reactivate your account?' : 'Are you sure?';
        const message = isUnpausing
            ? 'Your account will be active again and you will be visible to others. You can pause again anytime.'
            : 'Your profile will be hidden and your hosted events will be cancelled. You can reactivate anytime.';
        const yesText = isUnpausing
            ? (isProcessing ? 'Unpausing…' : 'Yes, unpause')
            : (isProcessing ? 'Pausing…' : 'Yes, pause');
        return (
            <div
                className="pause-account-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pause-account-modal-title"
            >
                <div className="pause-account-modal-card">
                    <h3 id="pause-account-modal-title" className="pause-account-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>{title}</h3>
                    <p className="pause-account-modal-message">{message}</p>
                    <div className="pause-account-modal-actions">
                        <button
                            type="button"
                            className="pause-account-modal-yes"
                            onClick={this.confirmAction}
                            disabled={isProcessing}
                            autoFocus
                        >
                            {yesText}
                        </button>
                        <button
                            type="button"
                            className="pause-account-modal-no"
                            onClick={this.closeConfirmModal}
                            disabled={isProcessing}
                        >
                            No, cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderProcessingOverlay() {
        const { isPaused } = this.state;
        const label = isPaused ? 'Unpausing your account…' : 'Pausing your account…';
        return (
            <div className="pause-account-processing-overlay" role="status" aria-live="polite">
                <div className="loading-spinner"></div>
                <p className="pause-account-processing-message">{label}</p>
            </div>
        );
    }

    render() {
        const { isPaused, showConfirmModal, isProcessing, successMessage, error } = this.state;
        const buttonLabel = isPaused ? 'Unpause Account' : 'Pause Account';
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content pause-account-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Pause Account</u></h2>

                        <div className="pause-account-card">
                            {successMessage && <p className="pause-account-success">{successMessage}</p>}
                            {error && <p className="pause-account-error">{error}</p>}

                            {isPaused ? this.renderPausedState() : this.renderActiveState()}

                            <button
                                type="button"
                                className="pause-account-button"
                                onClick={this.openConfirmModal}
                                disabled={isProcessing}
                            >
                                {buttonLabel}
                            </button>
                        </div>
                    </div>
                    {showConfirmModal && this.renderConfirmModal()}
                    {isProcessing && this.renderProcessingOverlay()}
                </div>
            </AuthGate>
        );
    }
}

export default PauseAccount;
